import { useEffect, useState } from 'react'
import {
    Typography,
    Button,
    Modal,
    ModalDialog,
    RadioGroup,
    Radio,
    Textarea,
    Box,
} from '@mui/joy'
import { Transition } from 'react-transition-group'
import { CheckCircle } from '@phosphor-icons/react'

import { useComplain } from '../../../hooks/service/useComplain'
import {
    complainReasons,
    COMPLAIN_OTHER_ID,
    COMPLAIN_TEXT_MIN_LENGTH,
} from '../../../data/complainReasons'

export default function ModalComplain({
    open = false,
    setOpen = () => { },
    complainFor,
    contentId,
}) {
    const submitComplain = useComplain();

    const [reason, setReason] = useState(null);
    const [reasonText, setReasonText] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const isOther = reason === COMPLAIN_OTHER_ID;

    useEffect(() => {
        if (open) {
            setReason(null);
            setReasonText('');
            setLoading(false);
            setError(null);
            setSuccess(false);
        }
    }, [open]);

    const canSubmit =
        reason !== null &&
        (!isOther || reasonText.trim().length >= COMPLAIN_TEXT_MIN_LENGTH);

    const handleSubmit = async () => {
        if (!canSubmit || loading) return;

        setLoading(true);
        setError(null);

        try {
            await submitComplain({
                complainFor,
                contentId,
                reason,
                reasonText: isOther ? reasonText.trim() : null,
            });

            setSuccess(true);
            setTimeout(() => setOpen(false), 1400);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Transition in={open} timeout={200}>
            {state => (
                <Modal
                    keepMounted
                    disableScrollLock
                    open={!['exited', 'exiting'].includes(state)}
                    onClose={() => setOpen(false)}
                    onClick={(e) => e.stopPropagation()}
                    slotProps={{
                        backdrop: {
                            sx: {
                                opacity: 0,
                                backdropFilter: 'none',
                                transition: 'opacity 200ms, backdrop-filter 200ms',
                                ...{
                                    entering: { opacity: 1, backdropFilter: 'blur(8px)' },
                                    entered: { opacity: 1, backdropFilter: 'blur(8px)' },
                                }[state],
                            },
                        },
                    }}
                    sx={[
                        state === 'exited'
                            ? { visibility: 'hidden' }
                            : { visibility: 'visible' },
                    ]}
                >
                    <ModalDialog
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                            border: 'none',
                            borderRadius: '32px',
                            p: 3,
                            opacity: 0,
                            width: '100%',
                            maxWidth: '420px',
                            position: 'fixed',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%) scale(0.95)',
                            transformOrigin: 'center center',
                            transition: 'opacity 300ms ease, transform 300ms ease',
                            ...{
                                entering: {
                                    opacity: 1,
                                    transform: 'translate(-50%, -50%) scale(1)'
                                },
                                entered: {
                                    opacity: 1,
                                    transform: 'translate(-50%, -50%) scale(1)'
                                },
                            }[state],
                        }}
                    >
                        {success ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <CheckCircle size={48} weight="fill" color="#1f9254" />
                                <Typography level="title-lg" sx={{ mt: 1 }}>
                                    Жалоба отправлена
                                </Typography>
                                <Typography level="body-sm" sx={{ mt: 0.5 }}>
                                    Спасибо, мы рассмотрим её в ближайшее время.
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography
                                    level="title-lg"
                                    sx={{ textAlign: 'center', mb: '-8px !important' }}
                                >
                                    Пожаловаться
                                </Typography>
                                <Typography level="body-sm" sx={{ mb: 1, textAlign: 'center' }}>
                                    Выберите причину жалобы
                                </Typography>

                                <Box
                                    sx={{
                                        maxHeight: '40vh',
                                        overflowY: 'auto',
                                        pr: 0.5,
                                        my: 1,
                                    }}
                                >
                                    <RadioGroup
                                        value={reason === null ? '' : reason}
                                        onChange={(e) => setReason(Number(e.target.value))}
                                        sx={{ gap: 0.5 }}
                                    >
                                        {complainReasons.map((item) => (
                                            <Radio
                                                key={item.value}
                                                value={item.value}
                                                label={item.name}
                                                sx={{ py: 0.6 }}
                                            />
                                        ))}
                                    </RadioGroup>

                                    <Textarea
                                        minRows={3}
                                        disabled={!isOther}
                                        placeholder='Опишите причину'
                                        value={reasonText}
                                        onChange={(e) => setReasonText(e.target.value)}
                                        sx={{
                                            py: '.75rem',
                                            px: '1rem',
                                            mt: 1,
                                            borderRadius: '20px',
                                            boxShadow: 'none',
                                            minHeight: 150,
                                            '& textarea': { flex: 1 },
                                        }}
                                    />
                                </Box>

                                {error && (
                                    <Typography color="danger" level="body-sm" sx={{ mt: 1 }}>
                                        {error}
                                    </Typography>
                                )}

                                <Button
                                    color="danger"
                                    onClick={handleSubmit}
                                    loading={loading}
                                    disabled={!canSubmit}
                                    sx={{ borderRadius: '50px', px: 2, py: 1.5, mt: 2, minHeight: '50px !important' }}
                                >
                                    Отправить жалобу
                                </Button>
                                <Button
                                    color="neutral"
                                    variant="outlined"
                                    onClick={() => setOpen(false)}
                                    sx={{ borderRadius: '50px', px: 2, py: 0.8 }}
                                >
                                    Отмена
                                </Button>
                            </>
                        )}
                    </ModalDialog>
                </Modal>
            )}
        </Transition>
    )
}
